import React from 'react';
import styled from 'styled-components';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import theme, { TOPIC_TO_COLOR } from '../../../theme';
import { CompanyResponse } from '../../../api/admin';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import { MinimalLearningPlanUnit } from '../Modals/EditLearnersLearningPlanModal';
import { SmallTooltip } from '../../Tooltip';

export interface OptionType {
  value: string;
  topic: string;
  type: LEARNING_PLAN_UNIT_TYPE;
  dropdownLabel: string;
  selectLabel: string;
  slug: string;
}

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const selectCustomStyles: StylesConfig<OptionType, true> = {
  menu: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.bgActive,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '8px',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 3,
  }),
  container: (provided) => ({
    ...provided,
    margin: 0,
    marginTop: 0,
    paddingBottom: 0,
    width: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    flexWrap: 'nowrap',
    overflow: 'auto',
    width: '100%',
    scrollbarWidth: 'none',
  }),
  dropdownIndicator: (provided) => ({ ...provided, display: 'none' }),
  clearIndicator: (provided) => ({ ...provided, display: 'none' }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: 'none',
  }),
  multiValue: (provided, { data }) => ({
    ...provided,
    backgroundColor: TOPIC_TO_COLOR[data.topic as keyof typeof TOPIC_TO_COLOR],
    borderRadius: '200px',
    padding: '4px 8px',
    gap: '6px',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    minWidth: 'unset',
    transition: 'all 0.3s ease-in-out',
  }),
  multiValueLabel: (provided, { data }) => {
    const tooltipId = `selected-${data.value}`;
    return {
      ...provided,
      textOverflow: 'unset',
      width: '100%',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '15px',
      lineHeight: '20px',
      fontFeatureSettings: "'tnum' on, 'lnum' on",
      color: theme.colors.textActive,
      textAlign: 'center',
      padding: 0,
      margin: 0,
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      '& > div > span': {
        dataTooltipId: tooltipId,
      },
    };
  },
  multiValueRemove: (provided) => ({
    ...provided,
    padding: 2,
    margin: 0,
    transition: 'all 0.3s ease-in-out',
  }),
  control: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    padding: 0,
    margin: 0,
    height: '100%',
    borderRadius: '8px',
    boxShadow: '0 !important',
    outline: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
    cursor: 'pointer',
  }),

  option: (
    provided,
    { isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    backgroundColor:
      isSelected || isFocused ? theme.colors.fieldBackground : 'transparent',
    color:
      isSelected || isFocused
        ? theme.colors.textPrimary
        : theme.colors.textRegular,
    cursor: isSelected ? 'not-allowed' : 'default',
    '&:active': {
      color: theme.colors.textPrimary,
      backgroundColor: theme.colors.fieldBackground,
    },
  }),

  singleValue: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  input: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme.colors.inputTextPlaceholder,
  }),
};

export function getLearningPlanOptions(
  modules: CompanyResponse['modules'],
  projects: CompanyResponse['projects']
) {
  return [
    ...modules.map((module) => ({
      type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
      value: `module-${module.contentId}`,
      slug: module.contentSlug,
      topic: module.topic,
      dropdownLabel: `[Module] ${module.contentTitle}${
        module.isDeprecated ? ' (Deprecated)' : ''
      }${module.isDraft ? ' (Draft)' : ''}`,
      selectLabel: module.contentTitle,
    })),
    ...projects.map((project) => ({
      type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
      value: `project-${project.projectId}`,
      slug: project.slug,
      topic: project.topic,
      dropdownLabel: `[${project.topic} Project] ${project.title}${
        project.isDraft ? ' (Draft)' : ''
      }`,
      selectLabel: project.title,
    })),
  ];
}

function getTooltip({
  unit,
  modules,
  projects,
}: {
  unit: MinimalLearningPlanUnit;
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
}): React.ReactNode {
  if (unit.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    const project = projects.find((p) => p.slug === unit.slug);
    return (
      <span>
        <b>{project?.topic.toUpperCase()} PROJECT</b>
      </span>
    );
  }

  const module = modules.find((m) => m.contentSlug === unit.slug);
  return (
    <span>
      <b>CONTENT MODULE</b>
      {module?.isDeprecated && (
        <span>
          <br /> (Deprecated)
        </span>
      )}
      {module?.isDraft && (
        <span>
          <br /> (Draft)
        </span>
      )}
    </span>
  );
}

export default function LearningPlanEditableCell({
  modules,
  projects,
  newLearningPlan,
  learnerId,
  onChange,
}: {
  modules: CompanyResponse['modules'];
  projects: CompanyResponse['projects'];
  newLearningPlan: MinimalLearningPlanUnit[];
  learnerId: number;
  onChange: (newLearningPlan: MinimalLearningPlanUnit[]) => void;
}) {
  const options: OptionType[] = getLearningPlanOptions(modules, projects);

  const formatOptionLabel = (
    option: OptionType,
    { context }: { context: 'menu' | 'value' }
  ) => {
    if (context === 'menu') {
      return option.dropdownLabel;
    }
    return (
      <span data-tooltip-id={`selected-${option.value}-${learnerId}`}>
        {option.selectLabel}
      </span>
    );
  };

  const selectedOptions: OptionType[] = (newLearningPlan ?? []).map((unit) => {
    const option = options.find((o) => o.slug === unit.slug);
    if (!option) {
      throw new Error(
        `Option not found for unit ${unit.type} ${
          unit.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
            ? unit.slug
            : unit.slug
        }`
      );
    }
    return {
      ...option,
    };
  });

  return (
    <React.Fragment>
      <SelectWrapper>
        <CreatableSelect
          options={options}
          value={selectedOptions}
          isMulti
          menuPortalTarget={document.body}
          placeholder="Add units to the learning plan.."
          isValidNewOption={() => false}
          styles={selectCustomStyles}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={(option: OptionType) => option.dropdownLabel}
          onChange={(newValue: readonly OptionType[]) => {
            const newLearningPlan = newValue.map((option) => ({
              type: option.type,
              slug: option.slug,
            }));
            onChange(newLearningPlan);
          }}
        />
      </SelectWrapper>
      {selectedOptions.map((option) => {
        const tooltipId = `selected-${option.value}-${learnerId}`;
        return (
          <SmallTooltip
            key={tooltipId}
            anchorSelect={`[data-tooltip-id="${tooltipId}"]`}
          >
            {getTooltip({
              unit: { type: option.type, slug: option.slug },
              modules,
              projects,
            })}
          </SmallTooltip>
        );
      })}
    </React.Fragment>
  );
}
